import React, { useState, useEffect, useCallback } from 'react';
import { feedsAPI } from '../services/api';
import NewsInterestSelector from '../components/NewsInterestSelector';
import './Feeds.css';

interface FeedSource {
  id: string;
  url: string;
  feed_type: string;
  source_name: string;
  city: string;
  state_province: string;
  country: string;
  news_interests: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface NewsItem {
  title: string;
  link: string;
  description: string;
  pub_date: string;
  source: string;
  image_url: string;
}

interface FormData {
  url: string;
  source_name: string;
  city: string;
  state_province: string;
  country: string;
  news_interests: string[];
  is_active: boolean;
  feed_type: string;
}

const emptyForm: FormData = {
  url: '',
  source_name: '',
  city: '',
  state_province: '',
  country: '',
  news_interests: [],
  is_active: true,
  feed_type: 'rss',
};

const Feeds: React.FC = () => {
  const [sources, setSources] = useState<FeedSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<FormData>(emptyForm);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormData>(emptyForm);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Preview state
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewItems, setPreviewItems] = useState<NewsItem[]>([]);
  const [previewSource, setPreviewSource] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchSources = useCallback(async () => {
    try {
      setError(null);
      const response = await feedsAPI.getAll(1, 100);
      setSources(response.data.sources || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load feed sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleAdd = async () => {
    try {
      await feedsAPI.create(addForm);
      setShowAddForm(false);
      setAddForm(emptyForm);
      fetchSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create feed source');
    }
  };

  const startEdit = (src: FeedSource) => {
    setEditingId(src.id);
    setEditForm({
      url: src.url,
      source_name: src.source_name,
      city: src.city,
      state_province: src.state_province,
      country: src.country,
      news_interests: src.news_interests || [],
      is_active: src.is_active,
      feed_type: src.feed_type || 'rss',
    });
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await feedsAPI.update(editingId, editForm);
      setEditingId(null);
      fetchSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update feed source');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await feedsAPI.delete(id);
      setDeletingId(null);
      fetchSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete feed source');
    }
  };

  const handlePreview = async (id: string) => {
    if (previewId === id) {
      setPreviewId(null);
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewId(id);
      const response = await feedsAPI.preview(id);
      setPreviewItems(response.data.items || []);
      setPreviewSource(response.data.source || '');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch preview');
      setPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="feeds-page">
      <div className="page-header">
        <h2>Feed Sources</h2>
        <button className="add-feed-btn" onClick={() => { setShowAddForm(!showAddForm); setEditingId(null); }}>
          {showAddForm ? 'Cancel' : '+ Add Feed'}
        </button>
      </div>

      {error && <div className="feeds-error"><p>{error}</p></div>}

      {loading ? (
        <div className="feeds-loading"><p>Loading feed sources...</p></div>
      ) : (
        <div className="feeds-table-container">
          <table className="feeds-table">
            <thead>
              <tr>
                <th>Source Name</th>
                <th>URL</th>
                <th>Type</th>
                <th>City</th>
                <th>State/Province</th>
                <th>Country</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showAddForm && (
                <>
                <tr className="feed-form-row">
                  <td><input value={addForm.source_name} onChange={e => setAddForm({...addForm, source_name: e.target.value})} placeholder="Source name" /></td>
                  <td><input value={addForm.url} onChange={e => setAddForm({...addForm, url: e.target.value})} placeholder="Feed URL" /></td>
                  <td>
                    <select value={addForm.feed_type} onChange={e => setAddForm({...addForm, feed_type: e.target.value})}>
                      <option value="rss">RSS</option>
                      <option value="scrape">Scrape</option>
                    </select>
                  </td>
                  <td><input value={addForm.city} onChange={e => setAddForm({...addForm, city: e.target.value})} placeholder="City" /></td>
                  <td><input value={addForm.state_province} onChange={e => setAddForm({...addForm, state_province: e.target.value})} placeholder="State/Province" /></td>
                  <td><input value={addForm.country} onChange={e => setAddForm({...addForm, country: e.target.value})} placeholder="Country" /></td>
                  <td>
                    <select value={addForm.is_active ? 'active' : 'inactive'} onChange={e => setAddForm({...addForm, is_active: e.target.value === 'active'})}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    <div className="form-actions">
                      <button className="save-btn" onClick={handleAdd}>Save</button>
                      <button className="cancel-btn" onClick={() => { setShowAddForm(false); setAddForm(emptyForm); }}>Cancel</button>
                    </div>
                  </td>
                </tr>
                <tr className="feed-interests-row">
                  <td colSpan={8}>
                    <NewsInterestSelector selectedInterests={addForm.news_interests} onChange={(interests) => setAddForm({...addForm, news_interests: interests})} />
                  </td>
                </tr>
                </>
              )}

              {sources.map(src => (
                editingId === src.id ? (
                  <React.Fragment key={src.id}>
                  <tr className="feed-form-row">
                    <td><input value={editForm.source_name} onChange={e => setEditForm({...editForm, source_name: e.target.value})} /></td>
                    <td><input value={editForm.url} onChange={e => setEditForm({...editForm, url: e.target.value})} /></td>
                    <td>
                      <select value={editForm.feed_type} onChange={e => setEditForm({...editForm, feed_type: e.target.value})}>
                        <option value="rss">RSS</option>
                        <option value="scrape">Scrape</option>
                      </select>
                    </td>
                    <td><input value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} /></td>
                    <td><input value={editForm.state_province} onChange={e => setEditForm({...editForm, state_province: e.target.value})} /></td>
                    <td><input value={editForm.country} onChange={e => setEditForm({...editForm, country: e.target.value})} /></td>
                    <td>
                      <select value={editForm.is_active ? 'active' : 'inactive'} onChange={e => setEditForm({...editForm, is_active: e.target.value === 'active'})}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </td>
                    <td>
                      <div className="form-actions">
                        <button className="save-btn" onClick={handleUpdate}>Save</button>
                        <button className="cancel-btn" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                  <tr className="feed-interests-row">
                    <td colSpan={8}>
                      <NewsInterestSelector selectedInterests={editForm.news_interests} onChange={(interests) => setEditForm({...editForm, news_interests: interests})} />
                    </td>
                  </tr>
                  </React.Fragment>
                ) : (
                  <tr key={src.id}>
                    <td>{src.source_name}</td>
                    <td className="feed-url" title={src.url}>{src.url}</td>
                    <td>{src.feed_type || 'rss'}</td>
                    <td>{src.city || '-'}</td>
                    <td>{src.state_province || '-'}</td>
                    <td>{src.country || '-'}</td>
                    <td>
                      <span className={`status-badge ${src.is_active ? 'active' : 'inactive'}`}>
                        {src.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {deletingId === src.id ? (
                        <div className="confirm-delete">
                          <span>Delete?</span>
                          <button className="confirm-yes" onClick={() => handleDelete(src.id)}>Yes</button>
                          <button className="confirm-no" onClick={() => setDeletingId(null)}>No</button>
                        </div>
                      ) : (
                        <div className="row-actions">
                          <button className="preview-btn" onClick={() => handlePreview(src.id)}>{previewId === src.id ? 'Close' : 'Preview'}</button>
                          <button onClick={() => startEdit(src)}>Edit</button>
                          <button className="delete-btn" onClick={() => setDeletingId(src.id)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              ))}

              {sources.length === 0 && !showAddForm && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>No feed sources found. Click "+ Add Feed" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {previewId && (
        <div className="preview-section">
          <div className="preview-header">
            <h3>{previewSource ? `Preview: ${previewSource}` : 'Preview'}</h3>
            <button className="preview-close" onClick={() => setPreviewId(null)}>Close</button>
          </div>
          {previewLoading ? (
            <div className="feeds-loading"><p>Loading preview...</p></div>
          ) : previewItems.length === 0 ? (
            <div className="feeds-loading"><p>No items found in this feed.</p></div>
          ) : (
            <div className="preview-grid">
              {previewItems.map((item, idx) => (
                <a key={idx} className="preview-card" href={item.link} target="_blank" rel="noopener noreferrer">
                  {item.image_url && (
                    <div className="preview-card-image">
                      <img src={item.image_url} alt="" />
                    </div>
                  )}
                  <div className="preview-card-body">
                    <h4 className="preview-card-title">{item.title}</h4>
                    {item.description && <p className="preview-card-desc">{item.description}</p>}
                    <div className="preview-card-meta">
                      <span className="preview-card-source">{item.source}</span>
                      {item.pub_date && <span className="preview-card-date">{new Date(item.pub_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Feeds;
